/**
 * In-cart upsell customizado — substitui o <foxkit-incart-upsell-custom> do app FoxKit.
 *
 * Como funciona:
 *  - A tag <foxkit-incart-upsell-custom> é renderizada UMA vez, logo acima do footer
 *    do carrinho (snippets/cart-drawer.liquid), carregando data-product-id do último
 *    item adicionado. Fica dentro de <m-cart-drawer-items>, que é re-renderizado a
 *    cada atualização do carrinho — então as recomendações se atualizam sozinhas.
 *  - Ele busca a section `incart-upsell` via API de recomendações da Shopify
 *    (routes.product_recommendations_url) com intent "related" — automático, sem
 *    configuração por produto. Produtos já no carrinho são filtrados no lado Liquid.
 *  - O bloco é um carrossel (um card por vez) com seletor de variante e botão
 *    "Adicionar". Ao adicionar, usa /cart/add.js e atualiza o drawer reaproveitando
 *    m-cart-drawer.onCartDrawerUpdate().
 */
(function () {
  "use strict";

  var SECTION_ID = "incart-upsell";
  // Troque para "complementary" se você configurar produtos complementares no
  // app gratuito "Search & Discovery" da Shopify. "related" funciona sem config.
  var INTENT = "related";
  var LIMIT = 10;

  // Evita conflito caso o app FoxKit ainda esteja ativo (ele também define esta tag).
  if (window.customElements && customElements.get("foxkit-incart-upsell-custom")) return;

  class FoxkitIncartUpsell extends HTMLElement {
    connectedCallback() {
      // Renderiza um único bloco de upsell por carrinho.
      var all = document.querySelectorAll("foxkit-incart-upsell-custom");
      if (all.length && all[0] !== this) {
        this.hidden = true;
        return;
      }
      this.productId = this.dataset.productId;
      if (!this.productId || !window.MinimogSettings) return;
      this.render();
    }

    get recommendationsUrl() {
      var base =
        window.MinimogSettings &&
        window.MinimogSettings.routes &&
        window.MinimogSettings.routes.product_recommendations_url;
      if (!base) return null;
      var params = new URLSearchParams({
        section_id: SECTION_ID,
        product_id: this.productId,
        limit: String(LIMIT),
        intent: INTENT,
      });
      return base + "?" + params.toString();
    }

    async render() {
      var url = this.recommendationsUrl;
      if (!url) return;
      try {
        var res = await fetch(url);
        if (!res.ok) return;
        var text = await res.text();
        var doc = new DOMParser().parseFromString(text, "text/html");
        var content = doc.querySelector(".m-incart-upsell");
        if (!content || !content.querySelector("[data-upsell-add]")) {
          this.hidden = true;
          return;
        }
        this.innerHTML = "";
        this.appendChild(content);
        this.hidden = false;
        this.bindEvents();
        this.initSlider();
      } catch (e) {
        console.error("[incart-upsell]", e);
      }
    }

    disconnectedCallback() {
      // O drawer re-renderiza o innerHTML no update do carrinho, removendo este
      // elemento. Destruímos o Swiper e o observer para não vazar listeners.
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
      if (this.swiper && typeof this.swiper.destroy === "function") {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
    }

    bindEvents() {
      var self = this;

      this.querySelectorAll("[data-upsell-add]").forEach(function (btn) {
        btn.addEventListener("click", function (e) {
          e.preventDefault();
          self.addToCart(btn);
        });
      });

      // Troca de variante: atualiza a variante do botão e o preço exibido.
      this.querySelectorAll("[data-upsell-variant]").forEach(function (select) {
        select.addEventListener("change", function () {
          var card = select.closest(".m-incart-upsell__item");
          if (!card) return;
          var btn = card.querySelector("[data-upsell-add]");
          if (btn) btn.dataset.variantId = select.value;
          var opt = select.options[select.selectedIndex];
          var priceEl = card.querySelector("[data-upsell-price]");
          if (priceEl && opt && opt.dataset.price) priceEl.textContent = opt.dataset.price;
        });
      });
    }

    initSlider() {
      var container = this.querySelector("[data-upsell-swiper]");
      if (!container) return;
      if (!window.MinimogLibs || !MinimogLibs.Swiper) return;

      var prevEl = this.querySelector("[data-upsell-prev]");
      var nextEl = this.querySelector("[data-upsell-next]");
      var pagination = this.querySelector("[data-upsell-pagination]");

      new MinimogLibs.Swiper(container, {
        slidesPerView: 1,
        spaceBetween: 10,
        threshold: 2,
        // Recalcula quando o drawer sai do off-screen / muda de tamanho.
        observer: true,
        observeParents: true,
        navigation:
          prevEl || nextEl
            ? { prevEl: prevEl, nextEl: nextEl, disabledClass: "swiper-button-disabled" }
            : false,
        pagination: pagination ? { el: pagination, type: "fraction" } : false,
      });

      // A instância real do Swiper fica no próprio elemento container.
      this.swiper = container.swiper;

      // O drawer é display:none quando fechado, então o Swiper pode medir largura 0
      // no init (ex.: carregar a página com item já no carrinho). Quando o drawer
      // abre, o container ganha dimensão — recalculamos o Swiper.
      var self = this;
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(function () {
          if (self.swiper && container.offsetWidth > 0 && typeof self.swiper.update === "function") {
            self.swiper.update();
          }
        });
        this.resizeObserver.observe(container);
      }
    }

    async addToCart(button) {
      var card = button.closest(".m-incart-upsell__item");
      var select = card && card.querySelector("[data-upsell-variant]");
      var variantId = (select && select.value) || button.dataset.variantId;
      if (!variantId || button.classList.contains("m-loading")) return;
      button.classList.add("m-loading");

      var config = fetchConfig("javascript");
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      delete config.headers["Content-Type"];
      var formData = new FormData();
      formData.append("id", variantId);
      formData.append("quantity", 1);
      config.body = formData;

      try {
        var res = await fetch(window.MinimogSettings.routes.cart_add_url, config);
        var data = await res.json();

        if (data.status) {
          this.notify(document.body, "appendChild", "error", data.message || data.description, true);
          return;
        }

        this.refreshCart();
        this.notify(
          document.querySelector("m-cart-drawer-items") || document.body,
          "prepend",
          "success",
          (window.MinimogStrings && window.MinimogStrings.itemAdded) || "Produto adicionado",
          false
        );

        if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
          window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, data);
        }
      } catch (e) {
        console.error("[incart-upsell]", e);
      } finally {
        button.classList.remove("m-loading");
      }
    }

    refreshCart() {
      var drawer = document.querySelector("m-cart-drawer");
      if (drawer && typeof drawer.onCartDrawerUpdate === "function") {
        drawer.onCartDrawerUpdate();
        drawer.open();
      } else {
        // Página do carrinho (sem drawer): recarrega para refletir o novo item.
        window.location.reload();
      }
    }

    notify(target, method, type, message, sticky) {
      if (!window.MinimogTheme || !window.MinimogTheme.Notification || !target || !message) return;
      window.MinimogTheme.Notification.show({
        target: target,
        method: method,
        type: type,
        message: message,
        last: 3000,
        sticky: sticky,
        delay: type === "success" ? 400 : 0,
      });
    }
  }

  customElements.define("foxkit-incart-upsell-custom", FoxkitIncartUpsell);
})();
